/**
 * The direction of harmony: where the journey has been, and what the next five cards
 * should therefore be *for*.
 *
 * ## The question this answers
 *
 * The owner's: "I want to make sure we think carefully about the direction of harmony, so
 * that we are on a considered journey not just passing from moment to moment." The
 * palette already guarantees five *contrasting* options. Contrast is not consideration —
 * five unrelated exits are as unconsidered as five substitutions, they are simply
 * unconsidered in a more colourful way. What this file adds is a reading of the moment
 * (is the pressure rising, standing, going out; how far from home; how long since the
 * phrase last touched the ground) and, from it, **a role per slot**: a set that contains
 * a release, a change of scenery, a continuation and one thing from outside the argument
 * entirely.
 *
 * ## Four things it deliberately is not
 *
 * 1. **Not a filter.** `ROLE_FIT` is a multiplier with a floor of `ROLE_FIT_FLOOR` and a
 *    ceiling of `ROLE_FIT_PEAK`, comparable to the band-fit weight it multiplies, so it
 *    tilts a set without deciding it. Nothing is ever zeroed — the same discipline
 *    `personalisationMultiplier` keeps, and for the same reason: a graph that narrows is
 *    a graph that has stopped being a graph.
 * 2. **Not a set-wide mood.** Every non-`open` shape carries, inside its first four
 *    slots, a role pulling against its own intent, plus a `free` slot where the model
 *    declines to have an opinion. `circling` offers exactly one way out of a loop
 *    somebody may be enjoying; `adrift` offers exactly one road home.
 * 3. **Not visible.** No badge, no colour, no ordering signal. The pattern card may be
 *    different; it must never be *better*. Role legibility comes from wording.
 * 4. **Not tuned.** Every number here is a table entry with a sentence behind it. There
 *    is no learned weight and no kernel.
 *
 * With no path the reading is `open`, whose shape is all `free` and whose every
 * `ROLE_FIT` entry is exactly 1 — so the engine's output is byte-for-byte what it was
 * before this file existed. That is the quiescence invariant, and it falls out of the
 * same table row as "we have only just started" rather than out of a special case.
 */

import { mod12 } from '../chord/enharmonic.js';
import type { PitchClass } from '../chord/types.js';
import type { KeyEstimate } from '../session/types.js';
import { notesOutsideKey } from './build.js';
import { KEY_SETTLED_CONFIDENCE } from './explain.js';
import type {
  Arc,
  DirectionReading,
  HeuristicContext,
  Intent,
  PatternMatch,
  Role,
  SlotRole,
} from './types.js';

/** Anything the tension model can read: a path chord, a current chord, a candidate. */
export interface TensionChord {
  rootPc: PitchClass;
  pitchClasses: readonly PitchClass[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** How many chords of the tail the arc is read over. */
export const ARC_WINDOW = 4;

/** A tension series this flat is not going anywhere. */
export const ARC_FLAT = 1;

/** And this much end-to-end movement is a direction rather than a wobble. */
export const ARC_STEP = 3;

/** At or below this, a chord counts as home for the phrase clock. */
export const HOME_TENSION = 1;

/**
 * Degrees that sit at home tension but are not where a phrase lands.
 *
 * One entry, and it is the deceptive cadence. `vi` scores 1 because it genuinely is
 * restful — it is a tonic substitute and the ear accepts it, which is exactly why V–vi
 * works — but the whole rhetorical point of a dodged cadence is that the phrase **failed**
 * to reach the ground. Letting it stop the clock made the two halves of this change
 * disagree about the same three chords: the pattern table calls G7–Am "the dodged
 * cadence" and the direction model read `landed · 0 since home`. It also meant
 * I–V–vi–IV, the most common pop progression there is, could never accumulate
 * `sinceHome` at all, so `phrase-overdue` could not fire in it.
 *
 * The tension itself is left alone. Lowering `DEGREE_TENSION.major[9]` would have fixed
 * the clock and broken the ear: C → Am would then read as a `press`.
 */
export const NOT_HOME_DEGREES: Readonly<Record<'major' | 'minor', readonly number[]>> =
  Object.freeze({
    major: Object.freeze([9] as const),
    // Nothing. `♭III` is at home tension in a minor key and it is the relative major,
    // which is a place a minor phrase genuinely comes to rest.
    minor: Object.freeze([] as const),
  });

/** Chords away from home before the phrase is overdue. Also the clock's cap. */
export const PHRASE_LONG = 8;

/** Below this much history the direction model declines to read anything. */
export const DIRECTION_MIN_PATH = 1;

/** A chord this tense is standing on something that wants to go. */
export const RELEASE_PRESSURE = 8;

/** And a chord this quiet has arrived. */
export const SETTLED_PRESSURE = 2;

/** This far out on the line of fifths and the way back is worth offering. */
export const FAR_FIFTHS = 4;

/** How much tension a candidate has to shed to count as an answer. */
export const ANSWER_DROP = 3;

/** And how near home it has to land while doing it. */
export const ANSWER_NEAR_HOME = 2;

/** How much a candidate has to add to count as winding up. */
export const PRESS_RISE = 2;

/** How far the scenery has to move to count as a turn. */
export const TURN_SHIFT = 2;

/** The ceiling on tension. Three terms, none of which can run away. */
export const TENSION_MAX = 12;

/** Role fit never falls below this — the direction model may not remove a candidate. */
export const ROLE_FIT_FLOOR = 0.6;

/** Nor lift one above this. Deliberately comparable to the band fit it multiplies. */
export const ROLE_FIT_PEAK = 1.8;

// ---------------------------------------------------------------------------
// Tension
// ---------------------------------------------------------------------------

/**
 * What a chord on each degree of the key *wants to do*.
 *
 * Not remoteness — remoteness is `homeFifths`, below, and it is a different axis. This is
 * how much a chord built on that degree leans. Read the major row: `I`=0 and `vi`=1 are
 * home, `iii`=2 is a weak tonic substitute, `ii`/`IV`/`♭VII`=3 are departures, `♭III` and
 * `♭VI`=4 are borrowed colour, `V` and `♭II`=5 are directed at something, `♯IV` and
 * `vii`=6 are unmoored. The minor row differs where the ear differs: `♭III`=1 because the
 * relative major is home-ish, `♭VI`=2 because it is diatonic and restful there.
 *
 * These are judgements. They are in one table so that disagreeing with one is a one-line
 * change rather than an argument with an algorithm.
 */
export const DEGREE_TENSION: Readonly<Record<'major' | 'minor', readonly number[]>> = Object.freeze(
  {
    //          I ♭II  II ♭III III  IV ♯IV   V ♭VI  VI ♭VII VII
    major: Object.freeze([0, 5, 3, 4, 2, 3, 6, 5, 4, 1, 3, 6]),
    minor: Object.freeze([0, 5, 4, 1, 4, 3, 6, 5, 2, 4, 3, 6]),
  },
);

/**
 * The sonority's own restlessness, before the key is consulted at all.
 *
 * First match wins, which is the whole of the logic: a diminished seventh is checked
 * before a plain diminished triad because it is one, and a dominant seventh before a
 * bare seventh for the same reason.
 *
 * Altered extensions are deliberately not read. A ♯9 and a ♭3 are the same pitch class,
 * so "how altered is this dominant" is not a question a pitch-class set can answer
 * honestly — and the ordering that matters (a dominant is tenser than a triad, a
 * half-diminished tenser than a minor seventh) survives without it.
 */
export function dissonanceOf(chord: TensionChord): number {
  const pcs = new Set(chord.pitchClasses);
  const has = (semitones: number): boolean => pcs.has(mod12(chord.rootPc + semitones));

  if (has(3) && has(6) && has(9)) return 4;
  if (has(4) && has(8) && !has(7)) return 3;
  if (has(4) && has(10)) return 3;
  if (has(3) && has(6)) return 3;
  if (has(11) || has(10) || (!has(3) && !has(4) && (has(2) || has(5)))) return 1;
  return 0;
}

function clampTension(value: number): number {
  return Math.min(TENSION_MAX, Math.max(0, value));
}

/**
 * How much a chord is leaning, 0..`TENSION_MAX`.
 *
 * Three terms, each of which a musician can argue with separately: what the degree wants,
 * what the sonority does on its own, and how much of it the key does not own.
 *
 * The property worth having is that G7 and B♭7 in C major score the same — 8 apiece. Two
 * dominants that both want C, one diatonic and one borrowed, are under the same amount of
 * pressure however different they sound, and a model that ranked them apart would offer a
 * release to one and not the other.
 *
 * **With no settled key, tension is dissonance alone.** The tonic is a guess below
 * `KEY_SETTLED_CONFIDENCE`, and a degree above a guess is noise; the two key-dependent
 * terms are unavailable and are not estimated.
 */
export function tensionOf(chord: TensionChord, keyEstimate: KeyEstimate): number {
  const dis = dissonanceOf(chord);
  if (keyEstimate.confidence < KEY_SETTLED_CONFIDENCE) return clampTension(dis);

  const degree = mod12(chord.rootPc - keyEstimate.tonicPc);
  const fn = (DEGREE_TENSION[keyEstimate.mode] as readonly number[])[degree] as number;
  const chrom = Math.min(3, notesOutsideKey(chord, keyEstimate).length);
  return clampTension(fn + dis + chrom);
}

// ---------------------------------------------------------------------------
// Distance
// ---------------------------------------------------------------------------

/**
 * Signed distance along the line of fifths, −6..+6. Sharpward is positive.
 *
 * Seven is its own inverse mod twelve, so multiplying the semitone distance by it maps
 * the chromatic circle onto the circle of fifths exactly, with no table.
 */
export function signedFifths(rootPc: PitchClass, tonicPc: PitchClass): number {
  const n = mod12((rootPc - tonicPc) * 7);
  return n > 6 ? n - 12 : n;
}

/**
 * How far from home a chord sits: the diatonic ones halved, the rest measured by the note
 * that left the key furthest behind.
 *
 * The discount is the musical judgement and it is the whole reason a raw line-of-fifths
 * distance is wrong here: the relative minor is four fifths from the tonic and shares six
 * notes out of seven with it, and nobody hears it as remote. Halving puts every wholly
 * diatonic chord inside ±2 and leaves everything borrowed outside where the halving would
 * have hidden it. The sign is kept, because sharpward-is-brightening and
 * flatward-is-darkening is a direction a player recognises even without a name for it.
 *
 * **The measurement is the chord's, not the root's**, and that is a correction rather
 * than a refinement. Measuring the root alone made the parallel minor report no movement
 * at all — Cm in C major is the archetypal darkening and it scored 0, as did C7, whose
 * whole event is a B♭ — and it inverted the ordering where it mattered, putting Fm (E♭
 * and A♭ borrowed) nearer home at −1 than plain B♭ major at −2. So a chord the key does
 * not own is placed by its **furthest outside note**: the one it borrowed, which is the
 * one the ear hears leave. Ties go to the flat side, because pitch classes cannot tell a
 * ♯ from a ♭ and the darker reading is the more conservative claim.
 *
 * That last sentence is also this function's known limit, and it is inherited rather than
 * introduced: `signedFifths` resolves each pitch class to its **nearest** reading on the
 * line, so pc 1 is D♭ at −5 and never C♯ at +7. A secondary dominant therefore reads as
 * remote and flatward — A7 in C major comes out −5 on the strength of its C♯ — where a
 * musician would call it two sharps out. Nothing a pitch-class set can do about it: the
 * path chords `direction.ts` reads have never been spelled and never will be. It costs
 * little, because the reading feeds a floored multiplier and an intent whose `adrift`
 * shape asks for a release first, which is what a secondary dominant wants anyway.
 *
 * Zero when the key is unsettled: the `turn` role then cannot fire, which is correct —
 * "the scenery changed" is not a claim you can make about a landscape nobody has mapped.
 */
export function homeFifths(chord: TensionChord, keyEstimate: KeyEstimate): number {
  if (keyEstimate.confidence < KEY_SETTLED_CONFIDENCE) return 0;

  const outside = notesOutsideKey(chord, keyEstimate);
  if (outside.length > 0) {
    let furthest = 0;
    for (const pc of outside) {
      const signed = signedFifths(pc, keyEstimate.tonicPc);
      const closer = Math.abs(signed) > Math.abs(furthest);
      const tied = Math.abs(signed) === Math.abs(furthest) && signed < furthest;
      if (closer || tied) furthest = signed;
    }
    return furthest;
  }

  // `Math.trunc(-0.5)` is negative zero, which `Object.is` distinguishes from zero and
  // `describeDirection` would print as "-0 from home". Neither is a distinction anybody
  // wants, so it is collapsed here rather than at every reader.
  const halved = Math.trunc(signedFifths(chord.rootPc, keyEstimate.tonicPc) / 2);
  return halved === 0 ? 0 : halved;
}

// ---------------------------------------------------------------------------
// The arc and the phrase clock
// ---------------------------------------------------------------------------

/** What a tension series is doing. First match wins. */
export function arcOf(tensions: readonly number[]): Arc {
  const n = tensions.length;
  if (n < 2) return 'opening';

  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const value of tensions) {
    min = Math.min(min, value);
    max = Math.max(max, value);
  }
  if (max - min <= ARC_FLAT) return 'static';

  const travelled = (tensions[n - 1] as number) - (tensions[0] as number);
  if (travelled >= ARC_STEP) return 'rising';
  if (travelled <= -ARC_STEP) return 'falling';
  return 'wandering';
}

/**
 * Is this the chord a phrase would stop on?
 *
 * Quiet enough, and not one of the quiet chords that are famously not arrivals. With no
 * settled key there is no tonic to be at and no degree to exclude, so home is read as a
 * chord with no dissonance of its own — the same substitution `tensionOf` makes one level
 * down.
 */
function isHome(chord: TensionChord, keyEstimate: KeyEstimate): boolean {
  if (tensionOf(chord, keyEstimate) > HOME_TENSION) return false;
  if (keyEstimate.confidence < KEY_SETTLED_CONFIDENCE) return true;
  const degree = mod12(chord.rootPc - keyEstimate.tonicPc);
  return !(NOT_HOME_DEGREES[keyEstimate.mode] as readonly number[]).includes(degree);
}

/**
 * How many chords since the phrase last touched the ground, capped at `PHRASE_LONG`.
 *
 * Walks the whole path rather than the four-chord arc window: "how long has this been
 * going on" is a longer question than "which way is it going", and a phrase that has not
 * been home for eight chords is a different situation from one that dipped four ago.
 */
export function sinceHomeOf(chords: readonly TensionChord[], keyEstimate: KeyEstimate): number {
  let count = 0;
  for (let i = chords.length - 1; i >= 0; i -= 1) {
    if (isHome(chords[i] as TensionChord, keyEstimate)) return count;
    count += 1;
    if (count >= PHRASE_LONG) return PHRASE_LONG;
  }
  return Math.min(count, PHRASE_LONG);
}

// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------

/**
 * What one candidate *does*, from the two chords and nothing else. First match wins.
 *
 * A deceptive resolution — G7 to Am, seven points of tension gone, landing one fifth from
 * home — classifies as `answer` rather than `turn`, and that is deliberate. It **is** an
 * answer; it is only not the expected one, and "not the expected one" is prose. The
 * pattern layer carries that meaning in a phrase bank, where it belongs, rather than
 * here in geometry that cannot express it.
 *
 * The order matters and follows the definitions: a candidate that sheds three points of
 * tension has released whatever else it does; one that adds two has wound up whatever
 * else it does; a `turn` is what is left when the scenery moves and the pressure does
 * not. So a chromatic chord that both brightens the room and raises the pressure — C to
 * A♭, six points up and four fifths out — reads as a `press`, because that is what the
 * ear does with it first.
 */
export function roleOf(
  current: TensionChord,
  target: TensionChord,
  keyEstimate: KeyEstimate,
): Role {
  const settled = keyEstimate.confidence >= KEY_SETTLED_CONFIDENCE;
  const delta = tensionOf(target, keyEstimate) - tensionOf(current, keyEstimate);

  if (delta <= -ANSWER_DROP) {
    // Without a key there is no "near home" to check, so an answer is a release alone.
    if (!settled || Math.abs(homeFifths(target, keyEstimate)) <= ANSWER_NEAR_HOME) return 'answer';
  }
  if (delta >= PRESS_RISE) return 'press';
  if (settled) {
    const moved = Math.abs(homeFifths(target, keyEstimate) - homeFifths(current, keyEstimate));
    if (moved >= TURN_SHIFT) return 'turn';
  }
  return 'hold';
}

// ---------------------------------------------------------------------------
// Intent
// ---------------------------------------------------------------------------

/** The conditions one rule can test. All present conditions must hold. */
interface IntentCondition {
  arc?: Arc;
  pressureAtLeast?: number;
  pressureAtMost?: number;
  sinceHomeAtLeast?: number;
  absHomeFifthsAtLeast?: number;
}

export interface IntentRule {
  id: string;
  when: IntentCondition;
  intent: Intent;
}

/**
 * Nine rules, six intents, read top to bottom, first match wins.
 *
 * `open` is both the neutral reading and the default, which matters twice. It makes the
 * quiescence invariant fall out of the same row as "we have only just started"; and it
 * means a `wandering` arc — where the model genuinely has nothing to say — produces
 * exactly today's engine rather than a guess dressed as a reading.
 *
 * Every rule has a fixture in `direction.test.ts` asserted by `id`, so a rule that can
 * never fire is visible rather than decorative.
 */
export const INTENT_RULES: readonly IntentRule[] = Object.freeze<IntentRule[]>([
  { id: 'setting-out', when: { arc: 'opening' }, intent: 'open' },
  { id: 'standing-on-tension', when: { pressureAtLeast: RELEASE_PRESSURE }, intent: 'at-the-top' },
  { id: 'phrase-overdue', when: { sinceHomeAtLeast: PHRASE_LONG }, intent: 'adrift' },
  { id: 'far-from-home', when: { absHomeFifthsAtLeast: FAR_FIFTHS }, intent: 'adrift' },
  { id: 'still-climbing', when: { arc: 'rising' }, intent: 'building' },
  {
    id: 'come-to-rest',
    when: { arc: 'falling', pressureAtMost: SETTLED_PRESSURE },
    intent: 'landed',
  },
  { id: 'coming-down', when: { arc: 'falling' }, intent: 'at-the-top' },
  { id: 'going-round', when: { arc: 'static' }, intent: 'circling' },
  { id: 'no-strong-reading', when: {}, intent: 'open' },
]);

/**
 * What a set of each intent should be made of, one role per palette slot.
 *
 * This is the answer to "a considered set of journeys, not five isolated exits", and it
 * is a table rather than a ranked preference for one reason: a ranked list biases every
 * slot the same way and produces a set that is all one mood, which then needs a
 * post-hoc repair. A role per slot has no defect to repair.
 *
 * Read them for what they refuse to do. `at-the-top` offers two releases and then a turn,
 * a press and a hold — five resolutions is as dull as five substitutions. `circling`
 * carries exactly one `press`: one door out of a loop somebody may be enjoying, never
 * five. `adrift` carries exactly one `answer`: the road home is one card, and at least
 * two of the others are not it. And every non-`open` shape has a `free` slot inside its
 * first four, which is the direction model's own escape hatch — the same idea as the
 * palette's uniform slot one level below.
 *
 * Six entries per shape because `branchCount` is 4..6; a shorter shape would silently
 * read `free` off the end, which is a defensible default but not an intended one.
 */
export const SET_SHAPES: Readonly<Record<Intent, readonly SlotRole[]>> = Object.freeze({
  open: Object.freeze(['free', 'free', 'free', 'free', 'free', 'free'] as const),
  building: Object.freeze(['press', 'turn', 'free', 'press', 'answer', 'turn'] as const),
  'at-the-top': Object.freeze(['answer', 'answer', 'free', 'turn', 'press', 'hold'] as const),
  landed: Object.freeze(['press', 'hold', 'free', 'turn', 'press', 'answer'] as const),
  circling: Object.freeze(['turn', 'hold', 'free', 'press', 'turn', 'hold'] as const),
  adrift: Object.freeze(['answer', 'turn', 'free', 'press', 'hold', 'turn'] as const),
});

/**
 * How much a slot wanting one role likes a candidate that does another.
 *
 * A table a musician can read and argue with rather than a kernel. The range —
 * `ROLE_FIT_FLOOR` to `ROLE_FIT_PEAK` — is deliberately comparable to the triangular band
 * fit it multiplies, so it tilts a slot without deciding it, and nothing is ever zero.
 */
export const ROLE_FIT: Readonly<Record<SlotRole, Readonly<Record<Role, number>>>> = Object.freeze({
  free: Object.freeze({ answer: 1.0, press: 1.0, turn: 1.0, hold: 1.0 }),
  answer: Object.freeze({ answer: 1.8, press: 0.6, turn: 1.0, hold: 0.8 }),
  press: Object.freeze({ answer: 0.6, press: 1.8, turn: 1.0, hold: 0.8 }),
  turn: Object.freeze({ answer: 0.9, press: 0.9, turn: 1.8, hold: 0.9 }),
  hold: Object.freeze({ answer: 0.8, press: 0.8, turn: 0.9, hold: 1.8 }),
});

function conditionHolds(
  when: IntentCondition,
  arc: Arc,
  pressure: number,
  sinceHome: number,
  fromHome: number,
): boolean {
  if (when.arc !== undefined && when.arc !== arc) return false;
  if (when.pressureAtLeast !== undefined && pressure < when.pressureAtLeast) return false;
  if (when.pressureAtMost !== undefined && pressure > when.pressureAtMost) return false;
  if (when.sinceHomeAtLeast !== undefined && sinceHome < when.sinceHomeAtLeast) return false;
  if (when.absHomeFifthsAtLeast !== undefined && Math.abs(fromHome) < when.absHomeFifthsAtLeast) {
    return false;
  }
  return true;
}

/**
 * Read the journey. No rng, no clock, no I/O — a pure function of the context.
 *
 * `DIRECTION_MIN_PATH` forces the arc to `opening`, which is not a special case dressed
 * up: a session with nothing behind it genuinely is opening, `setting-out` is the first
 * rule, and its intent is the neutral one. So the below-minimum reading and the
 * just-started reading are the same reading, reached the same way.
 */
export function readDirection(context: HeuristicContext): DirectionReading {
  const { keyEstimate } = context;
  const pressure = tensionOf(context.current, keyEstimate);
  const fromHome = homeFifths(context.current, keyEstimate);

  const window = context.tail.slice(-ARC_WINDOW);
  const arc: Arc =
    context.path.length < DIRECTION_MIN_PATH
      ? 'opening'
      : arcOf(window.map((chord) => tensionOf(chord, keyEstimate)));

  const sinceHome = sinceHomeOf([...context.path, context.current], keyEstimate);

  const rule =
    INTENT_RULES.find((candidate) =>
      conditionHolds(candidate.when, arc, pressure, sinceHome, fromHome),
    ) ?? (INTENT_RULES[INTENT_RULES.length - 1] as IntentRule);

  return {
    pressure,
    arc,
    homeFifths: fromHome,
    sinceHome,
    ruleId: rule.id,
    intent: rule.intent,
    shape: SET_SHAPES[rule.intent],
  };
}

/**
 * The whole reading on one line, for the question the owner always asks.
 *
 * "at-the-top · tension 8 · rising · +1 from home · 2 since home · two-five-major"
 *
 * Pure, and no UI: last round the question was "is the look-ahead even working?" and
 * answering it took a whole-body deep-equality test. This is the same question about this
 * feature, answerable by printing a string. Mounting it in the transparency sheet is
 * follow-on work — that sheet is `LlmDebugPayload`-shaped and would have to widen, which
 * is UI work in a change that has none.
 */
export function describeDirection(reading: DirectionReading, match: PatternMatch | null): string {
  const sign = reading.homeFifths >= 0 ? '+' : '';
  const parts = [
    reading.intent,
    `tension ${reading.pressure}`,
    reading.arc,
    `${sign}${reading.homeFifths} from home`,
    `${reading.sinceHome} since home`,
  ];
  if (match) parts.push(match.pattern.id);
  return parts.join(' · ');
}
